// ============================================================================
// src/services/newsApi.js - FSGU News/Posts API Service
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

// ============================================================================
// POSTS
// ============================================================================

/**
 * Fetch all news posts
 * @param {Object} filters - Optional filters
 * @returns {Promise<Array>} Array of post objects
 */
export const fetchPosts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }
    
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    if (filters.limit) {
      params.append('limit', filters.limit);
    }

    const url = `${API_BASE_URL}/get-posts.php${params.toString() ? '?' + params.toString() : ''}`;
    console.log('🔍 Fetching posts from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'API returned unsuccessful response');
    }
    
    console.log(`✅ Successfully fetched ${data.count} posts`);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching posts:', error);
    throw error;
  }
};

/**
 * Get a single post by ID
 * @param {number} postId - Post ID
 * @returns {Promise<Object>} Post object
 */
export const fetchPostById = async (postId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-post.php?id=${postId}`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Post not found');
    }

    return data.data;
  } catch (error) {
    console.error(`❌ Error fetching post ${postId}:`, error);
    throw error;
  }
};

/**
 * Create a new post
 * @param {Object} postData - Post data
 * @returns {Promise<Object>} Created post
 */
export const createPost = async (postData) => {
  try {
    console.log('📝 Creating new post:', postData);
    
    const response = await fetch(`${API_BASE_URL}/create-post.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postData)
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create post');
    }

    console.log('✅ Post created successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error creating post:', error);
    throw error;
  }
};

/**
 * Update an existing post
 * @param {number} postId - Post ID
 * @param {Object} postData - Updated post data
 * @returns {Promise<Object>} Updated post
 */
export const updatePost = async (postId, postData) => {
  try {
    console.log(`📝 Updating post ${postId}:`, postData);
    
    const response = await fetch(`${API_BASE_URL}/update-post.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...postData,
        id: postId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update post');
    }

    console.log('✅ Post updated successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error updating post:', error);
    throw error;
  }
};

/**
 * Delete a post
 * @param {number} postId - Post ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deletePost = async (postId) => {
  try {
    console.log(`🗑️ Deleting post ${postId}`);
    
    const response = await fetch(`${API_BASE_URL}/delete-post.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: postId })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete post');
    }

    console.log('✅ Post deleted successfully');
    return data;
    
  } catch (error) {
    console.error('❌ Error deleting post:', error);
    throw error;
  }
};

// ============================================================================
// CATEGORIES
// ============================================================================

/**
 * Fetch all categories
 * @returns {Promise<Array>} Array of category objects
 */
export const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-categories.php`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch categories');
    }

    return data.data;
  } catch (error) {
    console.error('❌ Error fetching categories:', error);
    throw error;
  }
};

/**
 * Create a new category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Created category
 */
export const createCategory = async (categoryData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/create-category.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(categoryData)
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create category');
    }

    return data.data;
  } catch (error) {
    console.error('❌ Error creating category:', error);
    throw error;
  }
};

/**
 * Update a category
 * @param {number} categoryId - Category ID
 * @param {Object} categoryData - Updated category data
 * @returns {Promise<Object>} Updated category
 */
export const updateCategory = async (categoryId, categoryData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update-category.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...categoryData,
        id: categoryId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update category');
    }

    return data.data;
  } catch (error) {
    console.error('❌ Error updating category:', error);
    throw error;
  }
};

/**
 * Delete a category
 * @param {number} categoryId - Category ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteCategory = async (categoryId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-category.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: categoryId })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete category');
    }

    return data;
  } catch (error) {
    console.error('❌ Error deleting category:', error);
    throw error;
  }
};

// Export API base URL
export { API_BASE_URL };